import React, {useState, useCallback, useEffect} from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import type {SelectChangeEvent} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type {GroupBy} from '../../pages/SourceLibraryPage';

export interface SourceItemFilterState {
  nodeId?: string | null;
  q?: string;
  types?: string[];
  extractionStatuses?: string[];
  summarizationStatuses?: string[];
  analysisStatuses?: string[];
  topic?: string;
  audience?: string;
  isProcess?: boolean | null;
  referencesProcess?: boolean | null;
  unclassified?: boolean;
}

interface SourceItemFilterBarProps {
  filter: SourceItemFilterState;
  onFilterChange: (filter: SourceItemFilterState) => void;
  groupBy: GroupBy;
  onGroupByChange: (groupBy: GroupBy) => void;
}

const TYPE_OPTIONS = [
  {value: 'pdf', label: 'PDF'},
  {value: 'docx', label: 'DOCX'},
  {value: 'html', label: 'Webpage'},
  {value: 'url', label: 'URL'},
  {value: 'image', label: 'Image'},
  {value: 'txt', label: 'Text'},
  {value: 'md', label: 'Markdown'},
];

const STATUS_OPTIONS = [
  {value: 'PENDING', label: 'Pending'},
  {value: 'PROCESSING', label: 'Processing'},
  {value: 'COMPLETED', label: 'Completed'},
  {value: 'FAILED', label: 'Failed'},
];

const ANALYSIS_STATUS_OPTIONS = [
  {value: 'PENDING', label: 'Pending'},
  {value: 'PROCESSING', label: 'Processing'},
  {value: 'COMPLETED', label: 'Completed'},
  {value: 'FAILED', label: 'Failed'},
];

const CLASSIFICATION_OPTIONS = [
  {value: '', label: 'Any'},
  {value: 'process', label: 'Process'},
  {value: 'not_process', label: 'Not a process'},
  {value: 'unclassified', label: 'Unclassified'},
];

const AUDIENCE_OPTIONS = [
  {value: '', label: 'Any'},
  {value: 'External', label: 'External'},
  {value: 'Internal', label: 'Internal'},
  {value: 'Both', label: 'Both'},
];

const REFERENCES_OPTIONS = [
  {value: '', label: 'Any'},
  {value: 'true', label: 'Yes'},
  {value: 'false', label: 'No'},
];

export default function SourceItemFilterBar({filter, onFilterChange, groupBy, onGroupByChange}: SourceItemFilterBarProps) {
  const [searchText, setSearchText] = useState(filter.q ?? '');

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== (filter.q ?? '')) {
        onFilterChange({...filter, q: searchText || undefined});
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]); // intentionally only depend on searchText

  const handleTypeChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const val = e.target.value;
    const types = typeof val === 'string' ? val.split(',') : val;
    onFilterChange({...filter, types: types.length > 0 ? types : undefined});
  }, [filter, onFilterChange]);

  const handleStatusChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const val = e.target.value;
    const statuses = typeof val === 'string' ? val.split(',') : val;
    onFilterChange({...filter, extractionStatuses: statuses.length > 0 ? statuses : undefined});
  }, [filter, onFilterChange]);

  const handleAnalysisStatusChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const val = e.target.value;
    const statuses = typeof val === 'string' ? val.split(',') : val;
    onFilterChange({...filter, analysisStatuses: statuses.length > 0 ? statuses : undefined});
  }, [filter, onFilterChange]);

  const handleClassificationChange = useCallback((e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    if (val === 'process') {
      onFilterChange({...filter, isProcess: true, unclassified: undefined});
    } else if (val === 'not_process') {
      onFilterChange({...filter, isProcess: false, unclassified: undefined});
    } else if (val === 'unclassified') {
      onFilterChange({...filter, isProcess: undefined, unclassified: true});
    } else {
      onFilterChange({...filter, isProcess: undefined, unclassified: undefined});
    }
  }, [filter, onFilterChange]);

  const handleReferencesChange = useCallback((e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    onFilterChange({...filter, referencesProcess: val === '' ? undefined : val === 'true'});
  }, [filter, onFilterChange]);

  const handleAudienceChange = useCallback((e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    onFilterChange({...filter, audience: val || undefined});
  }, [filter, onFilterChange]);

  // Derive the classification dropdown value from filter state
  const classificationValue = filter.unclassified ? 'unclassified'
    : filter.isProcess === true ? 'process'
    : filter.isProcess === false ? 'not_process'
    : '';

  return (
    <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5}}>
      <TextField
        size="small"
        placeholder="Search by name..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{fontSize: 18, color: '#9CA3AF'}} />
              </InputAdornment>
            ),
          },
        }}
        sx={{flex: 1, maxWidth: 300, '& .MuiOutlinedInput-root': {fontSize: 13}}}
      />
      <FormControl size="small" sx={{minWidth: 120}}>
        <InputLabel sx={{fontSize: 13}}>Type</InputLabel>
        <Select
          multiple
          value={filter.types ?? []}
          onChange={handleTypeChange}
          input={<OutlinedInput label="Type" />}
          renderValue={(selected) => (
            <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
              {(selected as string[]).map(v => (
                <Chip key={v} label={v.toUpperCase()} size="small" sx={{height: 18, fontSize: 10}} />
              ))}
            </Box>
          )}
          sx={{fontSize: 13}}
        >
          {TYPE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{fontSize: 13}}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{minWidth: 140}}>
        <InputLabel sx={{fontSize: 13}}>Extraction</InputLabel>
        <Select
          multiple
          value={filter.extractionStatuses ?? []}
          onChange={handleStatusChange}
          input={<OutlinedInput label="Extraction" />}
          renderValue={(selected) => (
            <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
              {(selected as string[]).map(v => (
                <Chip key={v} label={v} size="small" sx={{height: 18, fontSize: 10}} />
              ))}
            </Box>
          )}
          sx={{fontSize: 13}}
        >
          {STATUS_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{fontSize: 13}}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{minWidth: 140}}>
        <InputLabel sx={{fontSize: 13}}>Analysis</InputLabel>
        <Select
          multiple
          value={filter.analysisStatuses ?? []}
          onChange={handleAnalysisStatusChange}
          input={<OutlinedInput label="Analysis" />}
          renderValue={(selected) => (
            <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
              {(selected as string[]).map(v => (
                <Chip key={v} label={v} size="small" sx={{height: 18, fontSize: 10}} />
              ))}
            </Box>
          )}
          sx={{fontSize: 13}}
        >
          {ANALYSIS_STATUS_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{fontSize: 13}}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{minWidth: 140}}>
        <InputLabel sx={{fontSize: 13}}>Classification</InputLabel>
        <Select
          value={classificationValue}
          onChange={handleClassificationChange}
          input={<OutlinedInput label="Classification" />}
          sx={{fontSize: 13}}
        >
          {CLASSIFICATION_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{fontSize: 13}}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{minWidth: 120}}>
        <InputLabel sx={{fontSize: 13}}>Audience</InputLabel>
        <Select
          value={filter.audience ?? ''}
          onChange={handleAudienceChange}
          input={<OutlinedInput label="Audience" />}
          sx={{fontSize: 13}}
        >
          {AUDIENCE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{fontSize: 13}}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{minWidth: 120}}>
        <InputLabel sx={{fontSize: 13}}>References</InputLabel>
        <Select
          value={filter.referencesProcess === true ? 'true' : filter.referencesProcess === false ? 'false' : ''}
          onChange={handleReferencesChange}
          input={<OutlinedInput label="References" />}
          sx={{fontSize: 13}}
        >
          {REFERENCES_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{fontSize: 13}}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={groupBy}
        onChange={(_e, val) => { if (val) onGroupByChange(val); }}
        sx={{ml: 'auto'}}
      >
        <ToggleButton value="none" sx={{fontSize: 12, textTransform: 'none', py: 0.5, px: 1.5}}>
          None
        </ToggleButton>
        <ToggleButton value="source_page" sx={{fontSize: 12, textTransform: 'none', py: 0.5, px: 1.5}}>
          Source page
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
